import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Tooltip,
  IconButton,
  Autocomplete,
} from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon, Refresh, Visibility, Edit, AssignmentInd } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';

const ServiceRequestList = () => {
  const history = useHistory();
  const [filters, setFilters] = useState({
    search: '',
    priority: '',
    status: '',
    dateFrom: '',
    dateTo: '',
    customerText: '',
  });
  const [searchLoading, setSearchLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const rows = useMemo(() => ([
    { id: 'SR-1001', project: 'Rooftop A1', customer: 'Priya', date: '2025-10-29', priority: 'High', status: 'Open', assignedTo: '—' },
    { id: 'SR-1002', project: 'Ground C2', customer: 'Rahul', date: '2025-10-28', priority: 'Medium', status: 'In Progress', assignedTo: 'Amit' },
    { id: 'SR-0998', project: 'Battery B1', customer: 'Amit', date: '2025-10-25', priority: 'Low', status: 'Resolved', assignedTo: 'Riya' },
  ]), []);

  const customers = useMemo(() => ([
    { id: 'C-001', name: 'Priya', phone: '+91 90000 00011' },
    { id: 'C-002', name: 'Rahul', phone: '+91 90000 00022' },
    { id: 'C-003', name: 'Amit', phone: '+91 90000 00033' },
    { id: 'C-004', name: 'Riya', phone: '+91 90000 00044' },
  ]), []);

  const colorFor = (field, val) => {
    if (field === 'priority') {
      if (val === 'High') return 'error';
      if (val === 'Medium') return 'warning';
      return 'default';
    }
    if (field === 'status') {
      if (val === 'Resolved') return 'success';
      if (val === 'In Progress') return 'warning';
      return 'default';
    }
    return 'default';
  };

  const normalized = (s) => (s || '').toString().trim().toLowerCase();
  const stripSpaces = (s) => (s || '').toString().replace(/\s/g, '');

  const filtered = useMemo(() => {
    if (!appliedFilters) return [];
    return rows.filter(r => {
      const haystack = normalized(`${r.id} ${r.project} ${r.customer}`);
      const matchesSearch = !appliedFilters.search || haystack.includes(normalized(appliedFilters.search));

      const custText = normalized(appliedFilters.customerText);
      const matchesCustomer = !custText || normalized(r.customer).includes(custText) || customers.some(c => normalized(c.name) === normalized(r.customer) && stripSpaces(c.phone).includes(stripSpaces(appliedFilters.customerText)));

      const matchesPriority = !appliedFilters.priority || r.priority === appliedFilters.priority;
      const matchesStatus = !appliedFilters.status || r.status === appliedFilters.status;

      const fromOk = !appliedFilters.dateFrom || new Date(r.date) >= new Date(appliedFilters.dateFrom);
      const toOk = !appliedFilters.dateTo || new Date(r.date) <= new Date(appliedFilters.dateTo);

      return matchesSearch && matchesCustomer && matchesPriority && matchesStatus && fromOk && toOk;
    });
  }, [appliedFilters, rows, customers]);
  const displayed = hasSearched ? filtered : rows;

  const handleSearch = () => {
    setSearchLoading(true);
    setHasSearched(true);
    setAppliedFilters({ ...filters });
    setTimeout(() => setSearchLoading(false), 600);
  };
  const handleClear = () => {
    setFilters({ search: '', priority: '', status: '', dateFrom: '', dateTo: '', customerText: '' });
    setHasSearched(false);
    setSearchLoading(false);
    setAppliedFilters(null);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    setTimeout(() => setRefreshing(false), 500);
  };

  const onView = (row) => {
    alert(`View ${row.id} (demo)`);
  };
  const onEdit = (row) => {
    alert(`Edit ${row.id} (demo)`);
  };
  const onAssign = (row) => {
    history.push({ pathname: '/app/green-energy/service/assign-technician', search: `?sr=${encodeURIComponent(row.id)}` });
  };

  // Start with all rows shown; search applies filters when clicked

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Service Request List
            </Typography>
            <Typography variant="body2" color="text.secondary">View and manage installation/service requests</Typography>
          </Box>
          <Button startIcon={<Refresh />} variant="outlined" onClick={handleRefresh} disabled={refreshing}>{refreshing ? 'Refreshing...' : 'Refresh'}</Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField size="small" fullWidth label="Search (SR # / Project / Customer)" value={filters.search} onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                size="small"
                options={customers}
                getOptionLabel={(o) => o ? `${o.name} • ${o.phone}` : ''}
                renderInput={(params) => <TextField {...params} label="Customer (Suggest)" />}
                inputValue={filters.customerText}
                onInputChange={(_, value) => setFilters(prev => ({ ...prev, customerText: value }))}
                onChange={(_, value) => setFilters(prev => ({ ...prev, customerText: value ? value.name : '' }))}
                filterOptions={(opts, state) => {
                  const v = (state.inputValue || '').toLowerCase();
                  return opts.filter(c => `${c.name} ${c.phone}`.toLowerCase().includes(v));
                }}
                clearOnEscape
                freeSolo
                value={null}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select value={filters.priority} label="Priority" onChange={(e) => setFilters(prev => ({ ...prev, priority: e.target.value }))}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="High">High</MenuItem>
                  <MenuItem value="Medium">Medium</MenuItem>
                  <MenuItem value="Low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Status</InputLabel>
                <Select value={filters.status} label="Status" onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Open">Open</MenuItem>
                  <MenuItem value="In Progress">In Progress</MenuItem>
                  <MenuItem value="Resolved">Resolved</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField fullWidth size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={filters.dateFrom} onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField fullWidth size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={filters.dateTo} onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={1}>
              <Button fullWidth variant="contained"  onClick={handleSearch} disabled={searchLoading} sx={{ backgroundColor: '#536DFE', '&:hover': { backgroundColor: '#3F51B5' } }}>{searchLoading ? 'Searching...' : 'Search'}</Button>
            </Grid>
            <Grid item xs={12} md={1}>
              <Button fullWidth variant="outlined" onClick={handleClear}>Clear</Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#536DFE' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>SR #</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Project</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Customer</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Date</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Priority</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Assigned To</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, textAlign: 'center' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {hasSearched && displayed.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                      No results found
                    </TableCell>
                  </TableRow>
                )}
                {displayed.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ fontWeight: 700 }}>{r.id}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{r.project}</TableCell>
                    <TableCell>{r.customer}</TableCell>
                    <TableCell>{r.date}</TableCell>
                    <TableCell><Chip label={r.priority} size="small" color={colorFor('priority', r.priority)} /></TableCell>
                    <TableCell><Chip label={r.status} size="small" color={colorFor('status', r.status)} /></TableCell>
                    <TableCell>{r.assignedTo}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="View">
                        <IconButton size="small" onClick={() => onView(r)}><Visibility fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => onEdit(r)}><Edit fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Assign Technician">
                        <IconButton size="small" color="primary" onClick={() => onAssign(r)}><AssignmentInd fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ServiceRequestList;



